import { Component, NgZone, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';
import { ListCompanies } from '../models/company.model';
import { CompanyService } from './../services/company.service';
import { ListProjects, ListProjectZones } from '../models/project.model';
import { ProjectService } from './../services/project.service';
import { DeviceActivity, DeviceElement } from '../models/device.model';
import { DeviceService } from '../services/device.service';
import { MenusComponent } from './../menus/menus.component';
import { DialogService } from '../services/dialog.service';
import { TranslocoService } from '@jsverse/transloco';
import { ListUsers } from '../models/user.model';
import { UserService } from '../services/user.service';
import { StorageService } from '../services/storage.service';
import { AlarmConfigService } from '../services/alarm-config.service';
import { ListWorkers } from '../models/worker.model';
import { WorkerService } from '../services/worker.service';
import { DashboardService } from '../services/dashboard.service';
import { DeviceOperationalStatusKey, LatestTelemetryDevice } from '../models/dashboard.model';
import { buildDeviceStatus, deviceOperationalStatus, getNumericMeasure } from '../utils/telemetry-status';

@Component({
  selector: 'app-device',
  templateUrl: './device.component.html',
  styleUrl: './device.component.css'
})
export class DeviceComponent implements OnInit {

  constructor(
    public ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private projectService: ProjectService,
    private deviceService: DeviceService,
    private workerService: WorkerService,
    private userService: UserService,
    private alarmConfigService: AlarmConfigService,
    private menusComponent: MenusComponent,
    private dialogService: DialogService,
    private translocoService: TranslocoService,
    public storageService: StorageService,
    private dashboardService: DashboardService,
  ) { }

  public titleDevice = 'Dispositivo';
  public new = false;
  companyId: number = 0;

  lstCompanies:    ListCompanies[]    = [];
  lstProjects:     ListProjects[]     = [];
  lstProjectZones: ListProjectZones[] = [];
  lstWorkers:      ListWorkers[]       = [];
  lstCompanyUsers: ListUsers[]        = [];

  id_number_Input:     number  = 0;
  serial_number_Input: string  = '';
  assigned_Checked:    boolean = false;
  registered_Checked:  boolean = false;
  company_Selected:    number  = 0;
  project_Selected?:   number  = 0;
  zone_Selected?:      number  = 0;
  worker_Selected?:    number  = 0;
  lockSn: boolean = false;
  selectedTabIndex = 0;
  alarmAt?: string;
  activity: DeviceActivity[] = [];
  activityLoading = false;
  headerStatus: DeviceOperationalStatusKey | null = null;
  headerLastTelemetryAt: string | null = null;
  headerBattery: number | null = null;
  headerDevice: DeviceElement | null = null;
  headerLatestTelemetry: LatestTelemetryDevice | null = null;

  // ── Notification emails ───────────────────────────────────────────────────
  notificationEmailsList: { id: number; email: string; label: string | null; origin?: string }[] = [];
  emailToAdd = '';
  invalidEmailToAdd = false;

  // ── Notification contacts ─────────────────────────────────────────────────
  notificationContactsList: { id: number; user_id: number; first_name: string; last_name: string; origin?: string }[] = [];
  userToAdd: number | null = null;

  // ── Alarm retention ───────────────────────────────────────────────────────
  retentionTime: number | null = null;
  retentionTimeInitialized = false;

  device: DeviceElement = {
    id: 0, serial_number: '', assigned: false, registered: false, company_id: 0,
    project_id: undefined, zone_id: undefined, user_id: undefined,
  };

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.id_number_Input     = params['id'];
      this.companyId           = params['companyId'];
      this.serial_number_Input = params['serial_number'];
      this.alarmAt             = params['alarmAt'] || undefined;
      this.selectedTabIndex    = 0;
    });

    this.new = false;
    this.translocoService.selectTranslate('device.titleEdit').subscribe((t: string) => { this.titleDevice = t; });

    if (this.id_number_Input > 0 && this.companyId > 0) {
      this.getDeviceById();
    } else if (this.serial_number_Input) {
      this.lockSn = true;
      this.getDeviceBySn();
    } else {
      this.new = true;
      this.companyId = this.storageService.companyId;
      this.clearForm();
      this.fillForm();
      this.company_Selected = this.companyId;
      this.translocoService.selectTranslate('device.titleNew').subscribe((t: string) => { this.titleDevice = t; });
    }
  }

  fillForm(device?: any) {
    if (device) {
      this.companyId           = device.company_id;
      this.id_number_Input     = device.id;
      this.serial_number_Input = device.serial_number;
      this.assigned_Checked    = device.assigned;
      this.registered_Checked  = device.registered;
      this.company_Selected    = device.company_id;
      this.project_Selected    = device.project_id;
      this.zone_Selected       = device.zone_id;
      this.worker_Selected     = device.user_id;
      this.headerDevice        = device;
      this.loadOperationalHeader();
    }
    this.loadLists();
  }

  clearForm() {
    this.assigned_Checked   = false;
    this.registered_Checked = false;
    this.company_Selected   = 0;
    this.project_Selected   = 0;
    this.zone_Selected      = 0;
    this.worker_Selected    = 0;
  }

  loadNotificationData(): void {
    const deviceId  = this.id_number_Input;
    const companyId = this.companyId;

    this.deviceService.getNotificationEmails(deviceId, companyId).subscribe({
      next: (res: any) => { this.notificationEmailsList = res.data || []; },
      error: (e: any)  => console.error(e),
    });

    this.deviceService.getNotificationContacts(deviceId, companyId).subscribe({
      next: (res: any) => { this.notificationContactsList = res.data || []; },
      error: (e: any)  => console.error(e),
    });

    this.alarmConfigService.getAlarmConfig('D', deviceId).subscribe({
      next: (res: any) => { this.retentionTime = res?.retention_time ? res.retention_time / 60 : null; this.retentionTimeInitialized = true; },
      error: ()        => { this.retentionTime = null; this.retentionTimeInitialized = true; },
    });
  }

  saveRetentionTime(): void {
    if (this.retentionTime === null) {
      this.alarmConfigService.deleteAlarmConfig('D', +this.id_number_Input).subscribe({
        next: (res: any) => this.msgDialog(res.message, '', false, 'O'),
        error: (e: any)  => this.msgDialog('ERROR', e.error?.message, false, 'E'),
      });
      return;
    }
    const alarm: any = {
      alarm_type: 1,
      retention_time: this.retentionTime! * 60,
      parent_type: 'D',
      parent_id: +this.id_number_Input,
    };
    this.alarmConfigService.insertOrUpdateAlarm(alarm).subscribe({
      next: (res: any) => this.msgDialog(res.message, '', false, 'O'),
      error: (e: any)  => this.msgDialog('ERROR', e.error?.message, false, 'E'),
    });
  }

  onGuardarItm(form?: NgForm): void {
    if (form?.invalid || !(this.company_Selected > 0)) {
      form?.control.markAllAsTouched();
      return;
    }

    this.device = {
      id: this.id_number_Input,
      serial_number: this.serial_number_Input,
      assigned: this.assigned_Checked,
      registered: this.registered_Checked,
      company_id: this.company_Selected,
      project_id: this.project_Selected,
      zone_id: this.zone_Selected,
      user_id: this.worker_Selected,
    };

    const req = this.new
      ? this.deviceService.addDevice(this.device, this.companyId)
      : this.deviceService.updateDevice(this.id_number_Input, this.device, this.companyId);

    req.subscribe({
      next: (res: any) => { console.log(res.message); this.msgDialog(res.message, '', this.new, 'O'); },
      error: (e: any)  => { console.error(e); this.msgDialog('ERROR', e.error.message, false, 'E'); },
    });
  }

  getDeviceById() {
    this.deviceService.getDeviceById(this.id_number_Input, this.companyId).subscribe({
      next: (res: any) => {
        this.fillForm(res.data);
        this.loadCompanyUsers();
        this.loadNotificationData();
        this.loadActivity();
      },
      error: (e: any) => console.error(e),
    });
  }

  getDeviceBySn() {
    this.companyId = this.storageService.companyId;
    this.deviceService.getDeviceBySn(this.serial_number_Input).subscribe({
      next: (res: any) => {
        if (res) {
          this.new = false;
          this.translocoService.selectTranslate('device.titleEdit').subscribe((t: string) => { this.titleDevice = t; });
          console.log('Dispositivo encontrado', res.data);
          this.fillForm(res.data);
          this.loadCompanyUsers();
          this.loadNotificationData();
          this.loadActivity();
        } else {
          this.clearForm();
          this.fillForm();
          this.companyId = this.storageService.companyId;
          this.company_Selected = this.companyId;
          this.new = true;
          this.translocoService.selectTranslate('device.titleNew').subscribe((t: string) => { this.titleDevice = t; });
        }
      },
      error: (e: any) => this.msgDialog('ERROR', e.error.message, false, 'E'),
    });
  }

  loadLists() {
    this.lstCompanies    = this.companyService.getCompaniesListByRol(this.storageService.roleId, this.companyId);
    this.lstProjects     = this.projectService.getProjectsList(this.company_Selected || this.companyId);
    this.lstProjectZones = this.project_Selected
      ? this.projectService.getProjectZonesList(this.project_Selected, this.company_Selected || this.companyId)
      : [];
    this.workerService.getWorkersList(this.company_Selected || this.companyId).subscribe({
      next: workers => { this.lstWorkers = workers; },
      error: e => console.error(e),
    });
  }

  loadActivity(): void {
    this.activityLoading = true;
    this.deviceService.getDeviceActivity(this.id_number_Input, this.companyId).subscribe({
      next: res => { this.activity = res.data || []; this.activityLoading = false; },
      error: e => { console.error(e); this.activity = []; this.activityLoading = false; },
    });
  }

  loadOperationalHeader(): void {
    this.headerStatus = null;
    this.headerLastTelemetryAt = null;
    this.headerBattery = null;
    if (!this.companyId || !this.id_number_Input) return;
    this.dashboardService.getLatestTelemetry(this.companyId, [this.id_number_Input]).subscribe({
      next: telemetry => {
        const latest = telemetry[0] ?? null;
        this.headerLatestTelemetry = latest;
        const status = buildDeviceStatus(latest);
        this.headerStatus = deviceOperationalStatus(status);
        this.headerLastTelemetryAt = status.lastTelemetryAt;
        this.headerBattery = getNumericMeasure(latest, 'battery');
      },
    });
  }

  loadCompanyUsers(): void {
    this.userService.getNotifiableUsers(this.companyId).subscribe({
      next: (res: any) => {
        if (res.data) {
          this.lstCompanyUsers = res.data.map((u: any) => ({
            value:     +u.id,
            viewValue: u.first_name + ' ' + u.last_name,
            email:     u.email,
          }));
        }
      },
      error: (e: any) => console.error(e),
    });
  }

  // ── Notification email methods ────────────────────────────────────────────

  addNotificationEmail(): void {
    const email = this.emailToAdd.trim().toLowerCase();
    if (!this.isValidEmail(email)) { this.invalidEmailToAdd = true; return; }
    this.invalidEmailToAdd = false;

    this.deviceService.addNotificationEmail(this.id_number_Input, this.companyId, email).subscribe({
      next: (res: any) => {
        this.notificationEmailsList.push(res.data);
        this.emailToAdd = '';
      },
      error: (e: any) => this.msgDialog('ERROR', e.error?.message, false, 'E'),
    });
  }

  removeNotificationEmail(id: number): void {
    this.deviceService.deleteNotificationEmail(this.id_number_Input, this.companyId, id).subscribe({
      next: ()       => { this.notificationEmailsList = this.notificationEmailsList.filter(e => e.id !== id); },
      error: (e: any) => this.msgDialog('ERROR', e.error?.message, false, 'E'),
    });
  }

  isValidEmail(email: string): boolean { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); }

  // ── Notification contact methods ──────────────────────────────────────────

  addNotificationUser(): void {
    if (!this.userToAdd) return;
    const userId = this.userToAdd;

    this.deviceService.addNotificationContact(this.id_number_Input, this.companyId, userId).subscribe({
      next: (res: any) => {
        const user = this.lstCompanyUsers.find(u => u.value === userId);
        const parts = user ? user.viewValue.split(' ') : [];
        this.notificationContactsList.push({
          id:         res.data.id,
          user_id:    userId,
          first_name: parts[0] || '',
          last_name:  parts.slice(1).join(' ') || '',
        });
        this.userToAdd = null;
      },
      error: (e: any) => this.msgDialog('ERROR', e.error?.message, false, 'E'),
    });
  }

  removeNotificationUser(id: number): void {
    this.deviceService.deleteNotificationContact(this.id_number_Input, this.companyId, id).subscribe({
      next: ()        => { this.notificationContactsList = this.notificationContactsList.filter(c => c.id !== id); },
      error: (e: any) => this.msgDialog('ERROR', e.error?.message, false, 'E'),
    });
  }

  // ── Other actions ─────────────────────────────────────────────────────────

  msgDialog(msgShow: string, msg2Show: string, isOK: boolean, type: string) {
    this.dialogService.confirmDialog({
      title: 'Dispositivo',
      message: msgShow,
      message2: msg2Show,
      confirmText: 'OK',
      cancelText: '',
      showConfirmBox: false,
      dialogType: type,
    }).subscribe((yes) => {
      if (isOK) { setTimeout(() => { this.router.navigate(['devices']); }, 1000); }
    });
  }

  EliminarItm() {
    this.deviceService.deleteDevice(this.id_number_Input, this.companyId).subscribe({
      next: (res: any) => { console.log(res.message); this.msgDialog(res.message, '', true, 'O'); },
      error: (e: any)  => { console.error(e); this.msgDialog('ERROR', e.error.message, false, 'E'); },
    });
  }

  onEliminarItm() {
    this.dialogService.confirmDialog({
      title:          this.translocoService.translate('device.msgTitle'),
      message:        this.translocoService.translate('device.msgMessage'),
      message2:       this.translocoService.translate('device.msgMessage2'),
      confirmText:    this.translocoService.translate('device.confirmText'),
      cancelText:     this.translocoService.translate('device.cancelText'),
      showConfirmBox: true,
      dialogType:     'A',
    }).subscribe((yes) => {
      if (yes) { this.EliminarItm(); console.log('Si Eliminar.'); }
    });
  }

  onConfigItm() {
    this.dialogService.openConfiguration({
      device_id:     this.id_number_Input,
      company_id:    this.companyId,
      serial_number: this.serial_number_Input,
    });
  }

  onSelectCompany(companyId: number) {
    if (this.storageService.roleId) {
      this.company_Selected = companyId;
      this.project_Selected = 0;
      this.zone_Selected    = 0;
      this.lstProjects      = [];
      this.lstProjectZones  = [];
      this.worker_Selected = 0;
      this.lstProjects = this.projectService.getProjectsList(companyId);
      this.workerService.getWorkersList(companyId).subscribe({
        next: workers => { this.lstWorkers = workers; },
        error: e => console.error(e),
      });
    }
  }

  onSelectProject(proyectId: number) {
    this.zone_Selected   = 0;
    this.lstProjectZones = [];
    this.lstProjectZones = this.projectService.getProjectZonesList(proyectId, this.company_Selected || this.companyId);
  }

  onBaja() { console.log('dar de baja'); }

  scan() {
    this.dialogService.openScanner().subscribe((result: any) => {
      this.serial_number_Input = result.serial_number;
    });
  }
}
